import type { CheerioAPI } from "cheerio";
import type { ChapterRef } from "../../data-model/novel.js";
import type { ChapterIndex, Url } from "../../data-model/primitives.js";
import { unsafeBrandChapterIndex } from "../../data-model/primitives.js";
import { resolveUrl } from "../../utils/url.js";

export interface AnchorRefsOptions {
  readonly selector: string;
  readonly startIndex: ChapterIndex;
  readonly baseUrl: Url;
  /**
   * When true (default), use the anchor's `title` attribute, falling back to
   * its text. When false, use the anchor text only.
   */
  readonly preferTitleAttr?: boolean;
}

/**
 * Extract `ChapterRef`s from a list of anchors.
 *
 * INVARIANT: the returned indices are exactly `startIndex … startIndex+n-1`
 * where `n === result.length`. The counter advances ONLY on push; anchors with
 * no `href` or an empty title are skipped without consuming an index. This is
 * what lets callers safely advance the next page by `startIndex + refs.length`
 * without gaps or collisions.
 */
export function refsFromAnchors($: CheerioAPI, opts: AnchorRefsOptions): ChapterRef[] {
  const { selector, startIndex, baseUrl, preferTitleAttr = true } = opts;
  const refs: ChapterRef[] = [];
  let count = 0;
  $(selector).each((_, el) => {
    const a = $(el);
    const href = a.attr("href");
    if (!href) return;
    const text = a.text().replace(/\s+/g, " ").trim();
    const titleAttr = a.attr("title")?.trim();
    const title = preferTitleAttr ? titleAttr || text : text;
    if (!title) return;
    refs.push({
      index: unsafeBrandChapterIndex(startIndex + count),
      title,
      url: resolveUrl(baseUrl, href),
    });
    count++;
  });
  return refs;
}
